import { Button, Modal, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { ClientLocation } from '../../Models/ClientLocation';
import ClientApi from '../../Api/ClientApi';
import {
  fetchCountries,
  fetchLocationTypes,
  fetchStates,
} from '../../Actions/CommonFields';
import { SelectOption } from '../../Models/SelectOption';
import Loader from '../Common/Loader';
import { State } from '../../Models/State';

export default function ClientLocationModal({
  show,
  setShow,
  getClientLocations,
  clientId,
  selectedClientLocation,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  clientId: number;
  getClientLocations: (clientId: number) => Promise<void> | undefined;
  selectedClientLocation: ClientLocation | null;
}) {
  const dispatch = useAppDispatch();
  const { locationTypes, states, countries } = useAppSelector(
    (state) => state.commonFields
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [statesOptions, setstatesOptions] = useState<State[]>([]);

  const [formData, setFormData] = useState<ClientLocation>({
    id: 0,
    clientId: clientId,
    typeId: null,
    address: null,
    city: null,
    stateId: null,
    zip: null,
    contactName: null,
    contactEmail: null,
    contactPhone: null,
    clientSiteId: null,
    ponumber: null,
    notes: null,
    quickbooksId: null,
    countryId: null,

    clientLocationAssets: [],
    country: null,
    state: null,
    type: null,
  });

  const getReferences = async () => {
    if (!locationTypes.length || !states.length || !countries.length) {
      await Promise.all([
        dispatch(fetchLocationTypes()),
        dispatch(fetchStates()),
        dispatch(fetchCountries()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
      handleSetInitialValues();
    }
  }, [show]);

  useEffect(() => {
    handleStateOptions(formData.countryId ?? 0);
  }, [states]);

  function handleStateOptions(countryId: number) {
    const list = states.filter((x) => x.countryId === countryId);
    if (list) {
      setstatesOptions(list);
    } else {
      setstatesOptions([]);
    }
  }

  const handleSetInitialValues = () => {
    const values = selectedClientLocation
      ? {
          ...selectedClientLocation,
        }
      : {
          id: 0,
          clientId: clientId,
          typeId: null,
          address: null,
          city: null,
          stateId: null,
          zip: null,
          contactName: null,
          contactEmail: null,
          contactPhone: null,
          clientSiteId: null,
          ponumber: null,
          notes: null,
          quickbooksId: null,
          countryId: null,

          clientLocationAssets: [],
          country: null,
          state: null,
          type: null,
        };
    setFormData(values as ClientLocation);
    handleStateOptions(values.countryId ?? 0);
  };

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      clientId: clientId,
      typeId: null,
      address: null,
      city: null,
      stateId: null,
      zip: null,
      contactName: null,
      contactEmail: null,
      contactPhone: null,
      clientSiteId: null,
      ponumber: null,
      notes: null,
      quickbooksId: null,
      countryId: null,

      clientLocationAssets: [],
      country: null,
      state: null,
      type: null,
    };
    setFormData(values as ClientLocation);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'countryId') {
      setFormData((prevData) => ({ ...prevData, stateId: null }));
      handleStateOptions(Number(value ?? 0));
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    // setButtonDisabled(true);
    setIsLoading(true);
    if (!formData.stateId) {
      formData.stateId = null;
    }
    
    return await ClientApi.addUpdateClientLocation(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Save Success');
          setIsLoading(false);
          setButtonDisabled(false);
          getClientLocations(clientId);
          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error: ' + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    handleResetFormValues();
    setShow(false);
  }

  return (
    <Modal show={show} size='2xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  {selectedClientLocation ? 'Edit' : 'Add'} Location
                </h1>
              </div>
            </div>
          </div>

          <div>
            {isLoading && <Loader />}
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form id='clientLocationForm' onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='typeId'
                              name='typeId'
                              onChange={handleChangeSelect}
                              value={formData.typeId ?? ''}
                              sizing='md'
                              required
                              // disabled={
                              //   userModel?.userLevelId !== UserLevelNames.Admin
                              // }
                            >
                              <option value={''}>(Select)</option>
                              {locationTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Address:
                            </div>
                          </div>
                          <div className='flex flex-col'>
                            <div className='w-1/2'>
                              <Select
                                id='countryId'
                                name='countryId'
                                onChange={handleChangeSelect}
                                value={formData.countryId ?? ''}
                                sizing='md'
                              >
                                <option value=''>(Country)</option>
                                {countries.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='address'
                                name='address'
                                type='text'
                                placeholder='address...'
                                onChange={handleChangeInput}
                                value={formData.address ?? ''}
                                sizing='md'
                                required
                              />
                            </div>
                            <div className='flex w-full'>
                              <div className='w-1/3'>
                                <TextInput
                                  id='city'
                                  name='city'
                                  type='text'
                                  placeholder='city...'
                                  onChange={handleChangeInput}
                                  value={formData.city ?? ''}
                                  sizing='md'
                                  required
                                />
                              </div>
                              <div className='w-1/3'>
                                <Select
                                  id='stateId'
                                  name='stateId'
                                  onChange={handleChangeSelect}
                                  value={formData.stateId ?? ''}
                                  sizing='md'
                                  required
                                >
                                  <option>(State)</option>
                                  {statesOptions.map((state) => (
                                    <option key={state.id} value={state.id}>
                                      {state.name}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className='w-1/3'>
                                <TextInput
                                  id='zip'
                                  name='zip'
                                  type='text'
                                  placeholder='zip...'
                                  onChange={handleChangeInput}
                                  value={formData.zip ?? ''}
                                  sizing='md'
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Contact Name:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='contactName'
                              name='contactName'
                              type='text'
                              placeholder='contactName...'
                              onChange={handleChangeInput}
                              value={formData.contactName ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Contact Email:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='contactEmail'
                              name='contactEmail'
                              type='text'
                              placeholder='contactEmail...'
                              onChange={handleChangeInput}
                              value={formData.contactEmail ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Contact Phone:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='contactPhone'
                              name='contactPhone'
                              type='text'
                              placeholder='contactPhone...'
                              onChange={handleChangeInput}
                              value={formData.contactPhone ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Site Id:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='clientSiteId'
                              name='clientSiteId'
                              type='text'
                              placeholder='clientSiteId...'
                              onChange={handleChangeInput}
                              value={formData.clientSiteId ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              PO Number:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='ponumber'
                              name='ponumber'
                              type='text'
                              placeholder='ponumber...'
                              onChange={handleChangeInput}
                              value={formData.ponumber ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Notes:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='notes'
                              name='notes'
                              type='text'
                              placeholder='notes...'
                              onChange={handleChangeInput}
                              value={formData.notes ?? ''}
                              sizing='md'
                              autoComplete='new-password'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          form='clientLocationForm'
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
